import { GridColDef, GridFilterModel } from "@mui/x-data-grid";
import CustomDataGrid from "../../components/ui/customDataGrid/customDataGrid.component";
import { useTranslation } from "react-i18next";
import { Handshake, Print, CheckCircle, RequestQuote } from "@mui/icons-material";
import { CONTRACTS_ENDPOINT } from "../../consts";
import { useNavigate, useLocation } from "react-router-dom";
import { Chip, Tooltip } from "@mui/material";
import { useHasPermission } from "../../contexts/auth";
import EntityReferenceLink from "../../components/ui/entityReference/entityReferenceLink.component";

export default function ContractsList() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const canViewClients = useHasPermission("settlement.view_client");
  const canViewContracts = useHasPermission("settlement.view_contract");

  const initialSortModel = (() => {
    try {
      const params = new URLSearchParams(location.search);
      const ordering = params.get("ordering");
      if (!ordering) return undefined;
      return ordering.split(",").map((o) => ({ field: o.replace(/^-/, ""), sort: o.startsWith("-") ? ("desc" as const) : ("asc" as const) }));
    } catch (e) {
      return undefined;
    }
  })();

  const initialFilterModel: GridFilterModel | undefined = (() => {
    try {
      const params = new URLSearchParams(location.search);
      const endedParam = params.get("ended");
      if (endedParam !== "true" && endedParam !== "false") return undefined;
      return { items: [{ id: "ended-filter", field: "ended", operator: "is", value: endedParam === "true" }] };
    } catch (e) {
      return undefined;
    }
  })();

  const columns: GridColDef[] = [
    {
      field: "id",
      headerName: "ID",
    },
    {
      field: "number",
      headerName: t("Number"),
    },
    {
      field: "internal_name",
      headerName: t("Internal name"),
    },
    {
      field: "settlement_kinds",
      headerName: t("Settlement kind"),
      valueGetter: (value, row) => row.settlement_kinds?.join(", ") || "-",
    },
    {
      field: "client__name",
      headerName: t("Client"),
      editable: false,
      valueGetter: (value, row) => row.client?.name,
      renderCell: (params) => (
        <EntityReferenceLink
          entityType="client"
          entityId={params.row.client?.id}
          label={params.row.client?.name || "-"}
          canPreview={canViewClients}
        />
      ),
    },
    {
      field: "equipment_quantity",
      headerName: t("Equipments"),
      editable: false,
      renderHeader: () => (
        <Tooltip title={t("Equipments")}>
          <Print fontSize="small" />
        </Tooltip>
      ),
    },
    {
      field: "settlements_count",
      headerName: t("Ready to settle"),
      editable: false,
      renderHeader: () => (
        <Tooltip title={t("Ready to settle")}>
          <CheckCircle fontSize="small" />
        </Tooltip>
      ),
      renderCell: (params) => {
        const readyToSettle = Number(params.row.settlements_ready_to_settle_count || 0);
        const allSettlements = Number(params.row.settlements_count || 0);
        const ratio = allSettlements > 0 ? Math.min(readyToSettle / allSettlements, 1) : 0;

        return (
          <Chip
            label={`${readyToSettle}/${allSettlements}`}
            size="small"
            sx={(theme) => {
              const isDarkMode = theme.palette.mode === "dark";
              const saturation = isDarkMode ? 45 + ratio * 35 : 35 + ratio * 45;
              const lightness = isDarkMode ? 24 + ratio * 18 : 88 - ratio * 40;
              const background = `hsl(140 ${saturation}% ${lightness}%)`;

              return {
                bgcolor: background,
                color: theme.palette.getContrastText(background),
                fontWeight: 700,
                border: isDarkMode ? "1px solid rgba(255, 255, 255, 0.18)" : "1px solid rgba(0, 0, 0, 0.08)",
                "& .MuiChip-label": {
                  color: "inherit",
                },
              };
            }}
          />
        );
      },
    },
    {
      field: "has_pending_settlements",
      headerName: t("Has pending settlements"),
      editable: false,
      renderHeader: () => (
        <Tooltip title={t("Has pending settlements")}>
          <RequestQuote fontSize="small" />
        </Tooltip>
      ),
      valueGetter: (value, row) => (row.has_pending_settlements ? 1 : 0),
      renderCell: (params) => {
        const hasPending = Boolean(params.row.has_pending_settlements);
        return (
          <Chip
            label={hasPending ? t("Unsettled") : t("Settled")}
            size="small"
            sx={{
              bgcolor: hasPending ? "warning.light" : "success.light",
              color: hasPending ? "warning.contrastText" : "success.contrastText",
              fontWeight: 600,
              "& .MuiChip-label": {
                color: hasPending ? "warning.contrastText" : "success.contrastText",
              },
            }}
          />
        );
      },
    },
    {
      field: "start_date",
      headerName: t("Start Date"),
    },
    {
      field: "end_date",
      headerName: t("End Date"),
      valueGetter: (value, row) => row.end_date || t("Indefinite"),
    },
  ];

  const handleRowClick = (id: string) => {
    navigate(`/contracts/${id}`);
  };

  if (!canViewContracts) {
    return null;
  }

  return (
    <CustomDataGrid
      icon={<Handshake />}
      title={t("Contracts list")}
      url={CONTRACTS_ENDPOINT}
      gridSettingsKey="CONTRACTS"
      columns={columns}
      initialSortModel={initialSortModel}
      initialFilterModel={initialFilterModel}
      defaultHiddenFields={["ended"]}
      refreshEventName="contractAdded"
      onRowClick={handleRowClick}
      dataGridProps={{
        getRowClassName: (params) => (params.row.ended ? "contract-row-ended" : ""),
        sx: {
          "& .contract-row-ended": {
            opacity: 0.55,
          },
        },
      }}
    />
  );
}
